import React from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useI18n } from 'contexts/i18n'
import { useMe } from 'contexts/me'
import { useCheckDocumentPermission } from 'pages/documents/utilities'
import { getFullName } from 'utilities/person'
import { getPeopleProfileUrl } from 'utilities/url'
import { safeFormat } from 'utilities/date-time'
import { Content, Status, CaptionSmall, Note, Message } from './s'
import Progress from 'components/progress'
import { ParagraphSmall } from 'components/typography/paragraph'
import Link from 'components/link'
import Actions from './actions'
import { Plain } from 'components/button'
import { CrossCircleFilled as Error } from 'components/feather'

const SigningContent = ({ dokument, setReviewingDocumentSigningStatus, salt, ...props }) => {
    const { formatMessage } = useIntl()
    const { dateLocale: locale } = useI18n()

    const { me } = useMe()
    const { signable } = useCheckDocumentPermission()(dokument, true)

    const rejecter = dokument.signing.signatories.find(({ rejectedAt }) => !!rejectedAt)
    const signed = dokument.signing.signatories.filter(({ signedAt }) => !!signedAt)
    const total = dokument.signing.signatories.length

    const mySignatory = dokument.signing.signatories.find(({ user }) => (!!me?.id && (me?.id === user?.id)))
    const iHaveSigned = !!mySignatory?.signedAt

    const errorMessage = {
        rejected: {
            id: 'meta_rejected_by',
            defaultMessage: 'Rejected by {user}',
            values: {
                user: getFullName(rejecter?.user, { fallback: false })
            }
        },
        canceled: {
            id: 'meta_withdrawn_by',
            defaultMessage: 'Withdrawn by {user}',
            values: {
                user: getFullName(dokument.signing.updatedBy, { fallback: false })
            }
        },
        timedout: {
            id: 'document_signing_status_timedout_extended',
            defaultMessage: 'The signing process has passed its due date'
        }
    }[dokument.signing.status] ?? null

    return (
        <Content>
            {['closed', 'pending', 'preparation'].includes(dokument.signing.status) && (
                <>
                    <Status>
                        {(signed.length > 0) && (
                            <Progress
                                completed={signed.length}
                                total={total}
                                mode="compact" />
                        )}
                        <span>
                            <FormattedMessage
                                id="document_signing_count_of_total_signed"
                                defaultMessage="{count, plural, =0 {{count} of {total} have signed} =1 {{count} of {total} has signed} other {{count} of {total} have signed}}"
                                values={{
                                    count: signed.length,
                                    total
                                }} />
                        </span>
                        <Plain
                            className="constructive"
                            onClick={() => setReviewingDocumentSigningStatus({ dokument })}>
                            <FormattedMessage
                                id="action_view_details"
                                defaultMessage="View details" />
                        </Plain>
                    </Status>
                    {(dokument.signing.status === 'pending') && (
                        <div>
                            <CaptionSmall>
                                <FormattedMessage
                                    id="document_signing_sent"
                                    defaultMessage="Sent: {date} by {user}"
                                    values={{
                                        date: safeFormat(dokument.signing.sentAt, 'PP', { locale }),
                                        user: (
                                            <Link to={getPeopleProfileUrl(dokument.signing.owner)}>
                                                {getFullName(dokument.signing.owner)}
                                            </Link>
                                        )
                                    }} />
                            </CaptionSmall>
                            <CaptionSmall className="compact">
                                <FormattedMessage
                                    id="document_signing_deadline"
                                    defaultMessage="Signing deadline: {date}"
                                    values={{ date: safeFormat(dokument.signing.dueAt, 'PP', { locale }) }} />
                            </CaptionSmall>
                        </div>
                    )}
                    {(dokument.signing.status !== 'closed') && (
                        <>
                            {(!signable && !!iHaveSigned) && (
                                <Message
                                    type="success"
                                    message={formatMessage({
                                        id: 'documents_signing_message_you_have_signed',
                                        defaultMessage: 'You have signed the document.'
                                    })} />
                            )}
                            {(signable || !iHaveSigned) && (
                                <Message
                                    type="info"
                                    message={formatMessage({
                                        id: (signable || !mySignatory) ?
                                            'documents_signing_message_email_link_all' :
                                            'documents_signing_message_email_link',
                                        defaultMessage: (signable || !mySignatory) ?
                                            'All signatories have received a link via email where they can sign this document.' :
                                            'You have received a link via email where you can sign this document.'
                                    })} />
                            )}
                        </>
                    )}
                </>
            )}
            {['rejected', 'timedout', 'canceled'].includes(dokument.signing.status) && (
                <>
                    {!!errorMessage && (
                        <Status className="error">
                            <Error size={20} />
                            <FormattedMessage {...errorMessage} />
                        </Status>
                    )}
                    {(dokument.signing.status === 'timedout') && (
                        <ParagraphSmall className="compact error">
                            <FormattedMessage
                                id="document_signing_deadline"
                                defaultMessage="Signing deadline: {date}"
                                values={{ date: safeFormat(dokument.signing.dueAt, 'PP', { locale }) }} />
                        </ParagraphSmall>
                    )}
                    {!!rejecter?.rejectedReason && (
                        <Note className="compact">
                            {rejecter.rejectedReason}
                        </Note>
                    )}
                </>
            )}
            {signable && (
                <Actions
                    {...props}
                    dokument={dokument}
                    salt={salt} />
            )}
        </Content>
    )
}

export default SigningContent